import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import {
  AlignCenter, AlignLeft, AlignRight, Bold, Heading2, Heading3, ImagePlus, Italic, Link2, List, ListOrdered, Loader2,
  Minus, Pilcrow, Quote, Redo2, Strikethrough, Tags, Underline, Undo2, Unlink,
} from "lucide-react";

import { toHtml } from "@/lib/blogHtml";

function Tool({ on, active, title, testid, children, disabled }) {
  return (
    <button type="button" title={title} aria-label={title} data-testid={testid} disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={on}
      className={`grid h-8 w-8 place-items-center rounded-lg transition-colors disabled:opacity-40 ${active ? "bg-amber-500 text-black" : "text-slate-300 hover:bg-white/10"}`}>
      {children}
    </button>
  );
}

const Sep = () => <span className="mx-1 h-6 w-px bg-white/10" />;

export function RichEditor({ value, onChange, onUpload, uploading, docKey }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener", target: null } } }),
      Image.configure({ HTMLAttributes: { loading: "lazy" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Scrivi qui il tuo articolo… Usa i titoletti H2/H3 per organizzare il testo." }),
    ],
    content: toHtml(value),
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "blog-content min-h-[420px] px-5 py-4 focus:outline-none", "data-testid": "blog-editor" } },
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  useEffect(() => {
    if (editor) editor.commands.setContent(toHtml(value), { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey, editor]);

  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Indirizzo del link (es. /marketplace oppure https://…)", prev);
    if (url === null) return;
    if (!url) return c().extendMarkRange("link").unsetLink().run();
    const external = /^https?:\/\//.test(url) && !url.includes(window.location.host);
    c().extendMarkRange("link").setLink({ href: url, target: external ? "_blank" : null }).run();
  };
  const editAlt = () => {
    const alt = window.prompt("Testo alternativo (alt) dell'immagine", editor.getAttributes("image").alt || "");
    if (alt !== null) c().updateAttributes("image", { alt, title: alt }).run();
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const r = await onUpload(f);
    if (r) c().setImage({ src: r.url, alt: r.alt, title: r.alt }).run();
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#1C1F2C]" data-testid="rich-editor">
      <div className="sticky top-16 z-10 flex flex-wrap items-center gap-0.5 rounded-t-2xl border-b border-white/10 bg-[#161925] p-2">
        <Tool title="Paragrafo" testid="tb-paragraph" active={editor.isActive("paragraph")} on={() => c().setParagraph().run()}><Pilcrow className="h-4 w-4" /></Tool>
        <Tool title="Titoletto H2" testid="tb-h2" active={editor.isActive("heading", { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Tool>
        <Tool title="Titoletto H3" testid="tb-h3" active={editor.isActive("heading", { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Tool>
        <Sep />
        <Tool title="Grassetto" testid="tb-bold" active={editor.isActive("bold")} on={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></Tool>
        <Tool title="Corsivo" testid="tb-italic" active={editor.isActive("italic")} on={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></Tool>
        <Tool title="Sottolineato" testid="tb-underline" active={editor.isActive("underline")} on={() => c().toggleUnderline().run()}><Underline className="h-4 w-4" /></Tool>
        <Tool title="Barrato" testid="tb-strike" active={editor.isActive("strike")} on={() => c().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Tool>
        <Sep />
        <Tool title="Elenco puntato" testid="tb-bullet" active={editor.isActive("bulletList")} on={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></Tool>
        <Tool title="Elenco numerato" testid="tb-ordered" active={editor.isActive("orderedList")} on={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Tool>
        <Tool title="Citazione" testid="tb-quote" active={editor.isActive("blockquote")} on={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Tool>
        <Tool title="Linea separatrice" testid="tb-hr" on={() => c().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Tool>
        <Sep />
        <Tool title="Allinea a sinistra" testid="tb-left" active={editor.isActive({ textAlign: "left" })} on={() => c().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Tool>
        <Tool title="Centra" testid="tb-center" active={editor.isActive({ textAlign: "center" })} on={() => c().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Tool>
        <Tool title="Allinea a destra" testid="tb-right" active={editor.isActive({ textAlign: "right" })} on={() => c().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Tool>
        <Sep />
        <Tool title="Inserisci link" testid="tb-link" active={editor.isActive("link")} on={setLink}><Link2 className="h-4 w-4" /></Tool>
        <Tool title="Rimuovi link" testid="tb-unlink" disabled={!editor.isActive("link")} on={() => c().unsetLink().run()}><Unlink className="h-4 w-4" /></Tool>
        <Tool title="Carica immagine" testid="tb-image" disabled={uploading} on={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        </Tool>
        <Tool title="Modifica testo alternativo immagine" testid="tb-alt" disabled={!editor.isActive("image")} on={editAlt}><Tags className="h-4 w-4" /></Tool>
        <Sep />
        <Tool title="Annulla" testid="tb-undo" disabled={!editor.can().undo()} on={() => c().undo().run()}><Undo2 className="h-4 w-4" /></Tool>
        <Tool title="Ripeti" testid="tb-redo" disabled={!editor.can().redo()} on={() => c().redo().run()}><Redo2 className="h-4 w-4" /></Tool>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} data-testid="blog-image-input" />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
